import { SiteUrls } from '@tableau/shared-urls';
import { IDataQualityIndicator } from '@tableau/vizportal-webclient-api/lib/api/types/IDataQualityIndicator';
import React from 'react';
import { DataQualityWarningAssetFragment, DataQualityWarningFragment } from '../../../../../../gen-src/queries';
import { DataQualityCertificationGraphqlOrIndicator } from '../../dataQualityIndicators/DataQualityIndicatorFragments';
import { DataQualityIndicatorAssetType } from '../../dataQualityIndicators/DataQualityIndicatorSharedHelpers';
import { CertificationStatusChipOrIcon, WarningStatusIcon } from '../../dataQualityIndicators/DataQualityIndicatorStatusChip';

import { DataQualityStatusChip } from '../../dataQualityWarnings/DataQualityStatusChip';
import {
  DataQualityWarningGraphqlOrIndicator,
  mapActiveFragmentsOrIndicatorsToDetails,
} from '../../dataQualityWarnings/DataQualityWarningFragments';
import { divWithStyle } from '../../style/LayoutComponents';
import { SiteUrlsContext } from '../../urls/SiteUrlsContext';
import { TypedBodyCellProps } from '../TypedDataGridInterfaces';
import { cellPaddingStyle } from './CellStyles';

const IconCellDiv = divWithStyle('IconCell', {
  ...cellPaddingStyle,
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
});

const DisabledIconCellDiv = divWithStyle('DisabledIconCell', {
  ...cellPaddingStyle,
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  opacity: 0.6,
});

interface DataQualityCellProps {
  readonly dataQualityWarnings?: ReadonlyArray<DataQualityWarningGraphqlOrIndicator>;
  readonly disabled?: boolean;
  readonly linksDisabled?: boolean;
  readonly stackingRootId?: string;
  readonly urls?: SiteUrls;
  readonly disableIconOnlyTabIndex?: boolean;
}

/** @deprecated TFS1478812 */
function DataQualityCell(props: DataQualityCellProps) {
  if (!props || !props.dataQualityWarnings || !props.dataQualityWarnings.some(w => w.isActive)) {
    return null;
  }

  const details = mapActiveFragmentsOrIndicatorsToDetails(props.dataQualityWarnings, true, props.urls);
  if (details.length === 0) {
    return null;
  }

  return (
    props.disabled ? (
      <DisabledIconCellDiv>
        <DataQualityStatusChip
          details={details}
          iconOnly={true}
          linksDisabled={props.linksDisabled || false}
          stackingRootId={props.stackingRootId}
          iconOnlyTabIndex={props.disableIconOnlyTabIndex ? -1 : 0}
        />
      </DisabledIconCellDiv>
    ) : (
      <IconCellDiv>
        <DataQualityStatusChip
          details={details}
          iconOnly={true}
          linksDisabled={props.linksDisabled || false}
          stackingRootId={props.stackingRootId}
          iconOnlyTabIndex={props.disableIconOnlyTabIndex ? -1 : 0}
        />
      </IconCellDiv>
    )
  );
}

const IconStyle: React.CSSProperties = {
  position: 'relative',
  left: '13.4px',
  top: '2px'
};

function convertDataQualityWarningsFromRowDataToIDataQualityIndicator(oldWarnings: readonly DataQualityWarningGraphqlOrIndicator[] | undefined): IDataQualityIndicator[] | undefined {
  const newWarnings: IDataQualityIndicator[] | undefined = oldWarnings?.map((warning: DataQualityWarningGraphqlOrIndicator) => {
    const owner = 'owner' in warning ? warning.owner : {
      authId: '',
      displayName: warning.author?.name ? warning.author?.name : '',
      username: warning.author?.username ? warning.author?.username : '',
      domainName: warning.author?.domain ? warning.author?.domain : '',
      id: '',
    }
    const newWarning: IDataQualityIndicator = {
      isActive: warning.isActive,
      isSevere: warning.isSevere,
      type: 'type' in warning ? warning.type : warning.warningType,
      id: 'vizportalId' in warning ? warning.vizportalId : '',
      message: warning.message ? warning.message : '',
      updatedAt: new Date(warning.updatedAt),
      owner: owner,
    }
    return newWarning;
  });

  return newWarnings;
}

function convertDataQualityCertificationsFromRowDataToDataQualityIndicator(oldCertifications: readonly DataQualityCertificationGraphqlOrIndicator[]): IDataQualityIndicator[] | undefined {
  const newCertifications: IDataQualityIndicator[] | undefined = oldCertifications?.map((certification: DataQualityCertificationGraphqlOrIndicator) => {
    const owner = 'owner' in certification ? certification.owner : {
      authId: '',
      displayName: certification.author?.name ? certification.author?.name : '',
      username: certification.author?.username ? certification.author?.username : '',
      domainName: certification.author?.domain ? certification.author?.domain : '',
      id: '',
    }
    const newCertification: IDataQualityIndicator = {
      isActive: certification.isActive,
      isSevere: true,
      type: 'CERTIFIED',
      id: 'vizportalId' in certification ? certification.vizportalId : '',
      message: certification.message ? certification.message : '',
      updatedAt: new Date(certification.updatedAt),
      owner: owner,
    }
    return newCertification;
  });

  return newCertifications;
}

export type DataQualityCellFormatterRowDataFunctions<T> = {
  readonly getAssetUrlFromRowData: (urls: SiteUrls, rowData: T) => string
  readonly getNameFromRowData: (rowData: T) => string;
  readonly getTypeNameFromRowData: (rowData: T) => DataQualityIndicatorAssetType;
  readonly getDataQualityCertificationFromRowData: (rowData: T) => ReadonlyArray<DataQualityCertificationGraphqlOrIndicator>;

}

export type DataQualityCellFormatterOpts<T> = {
  getWarningsFromRowData: (rowData: T) => ReadonlyArray<DataQualityWarningGraphqlOrIndicator> | undefined;
  getChildWarningsFromRowData?: (rowData: T) => ReadonlyArray<DataQualityWarningFragment & DataQualityWarningAssetFragment> | undefined;
  getDisabledFromRowData?: (rowData: T) => boolean;
  linksDisabled?: boolean;
  stackingRootId?: string;
  disableIconOnlyTabIndex?: boolean;
  isCertification?: boolean;
  rowDataFunctions?: DataQualityCellFormatterRowDataFunctions<T>;
}

/** @deprecated TFS1478812 */
export function createDataQualityCellFormatter<T>(opts: DataQualityCellFormatterOpts<T>): React.Factory<TypedBodyCellProps<T>> {
  const { getWarningsFromRowData, getDisabledFromRowData, getChildWarningsFromRowData, linksDisabled, stackingRootId, disableIconOnlyTabIndex, isCertification, rowDataFunctions } = opts;
  // eslint-disable-next-line react/display-name
  return (props: TypedBodyCellProps<T>) => {
    if (isCertification && rowDataFunctions) {
      const oldCertifications = rowDataFunctions.getDataQualityCertificationFromRowData(props.row.data);
      const newCertifications = convertDataQualityCertificationsFromRowDataToDataQualityIndicator(oldCertifications);
      return (linksDisabled ?
        <div style={IconStyle}>
          <CertificationStatusChipOrIcon
            subjectArea={rowDataFunctions.getTypeNameFromRowData(props.row.data)}
            kind={'vizportal'}
            anchoredAsset={{
              assetName: rowDataFunctions.getNameFromRowData(props.row.data),
              assetType: rowDataFunctions.getTypeNameFromRowData(props.row.data),
              assetUrl: _ => '',
            }}
            anchoredCertifications={newCertifications ? newCertifications : []}
            iconOnly={true}
            linksDisabled={linksDisabled}
          />
        </div>
        :
        <SiteUrlsContext.Consumer>
          {urls => (
            <div style={IconStyle}>
              <CertificationStatusChipOrIcon
                subjectArea={rowDataFunctions.getTypeNameFromRowData(props.row.data)}
                kind={'vizportal'}
                anchoredAsset={{
                  assetName: rowDataFunctions.getNameFromRowData(props.row.data),
                  assetType: rowDataFunctions.getTypeNameFromRowData(props.row.data),
                  assetUrl: u => rowDataFunctions.getAssetUrlFromRowData(u, props.row.data),
                }}
                anchoredCertifications={newCertifications ? newCertifications : []}
                iconOnly={true}
                urls={linksDisabled ? undefined : urls}
                linksDisabled={linksDisabled}
              />
            </div>
          )}
        </SiteUrlsContext.Consumer>
      );
    } else if (rowDataFunctions) {
      const oldWarnings = getWarningsFromRowData(props.row.data);
      const newWarnings: IDataQualityIndicator[] | undefined = convertDataQualityWarningsFromRowDataToIDataQualityIndicator(oldWarnings);
      const anchoredChildWarnings = getChildWarningsFromRowData ? getChildWarningsFromRowData(props.row.data) : []
      return (linksDisabled ?
        <div style={IconStyle}>
          <WarningStatusIcon
            iconOnly={true}
            kind={'vizportal'}
            upstreamWarnings={[]}
            subjectArea={rowDataFunctions.getTypeNameFromRowData(props.row.data)}
            anchoredAsset={{
              assetName: rowDataFunctions.getNameFromRowData(props.row.data),
              assetType: rowDataFunctions.getTypeNameFromRowData(props.row.data),
              assetUrl: _ => '',
            }}
            anchoredChildWarnings={anchoredChildWarnings}
            anchoredWarnings={newWarnings ? newWarnings : []}
            linksDisabled={linksDisabled}
          />
        </div>
        :
        <SiteUrlsContext.Consumer>{urls => (
          <div style={IconStyle}>
            <WarningStatusIcon
              iconOnly={true}
              kind={'vizportal'}
              upstreamWarnings={[]}
              subjectArea={rowDataFunctions.getTypeNameFromRowData(props.row.data)}
              anchoredAsset={{
                assetName: rowDataFunctions.getNameFromRowData(props.row.data),
                assetType: rowDataFunctions.getTypeNameFromRowData(props.row.data),
                assetUrl: u => rowDataFunctions.getAssetUrlFromRowData(u, props.row.data),
              }}
              anchoredChildWarnings={anchoredChildWarnings}
              anchoredWarnings={newWarnings ? newWarnings : []}
              urls={linksDisabled ? undefined : urls}
              linksDisabled={linksDisabled}
            />
          </div>
        )}</SiteUrlsContext.Consumer>
      );
    }

    if (linksDisabled) {
      return (
        <DataQualityCell
          dataQualityWarnings={getWarningsFromRowData(props.row.data)}
          disabled={getDisabledFromRowData ? getDisabledFromRowData(props.row.data) : false}
          linksDisabled={linksDisabled}
          stackingRootId={stackingRootId}
          disableIconOnlyTabIndex={disableIconOnlyTabIndex}
        />
      );
    } else {
      return (
        <SiteUrlsContext.Consumer>
          {urls => (
            <DataQualityCell
              dataQualityWarnings={getWarningsFromRowData(props.row.data)}
              disabled={getDisabledFromRowData ? getDisabledFromRowData(props.row.data) : false}
              linksDisabled={linksDisabled}
              stackingRootId={stackingRootId}
              urls={urls}
              disableIconOnlyTabIndex={disableIconOnlyTabIndex}
            />
          )
          }
        </SiteUrlsContext.Consumer>
      );
    }
  };
}
